import React from 'react';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import {
  Card,
  Input,
  Label,
  Message,
  Radio,
  Select,
  Tooltip,
} from '@iwoca/orion';
import { useFormContext } from 'react-hook-form';
import { useSignupData } from '../hooks/useSignupData';
import { TSignupFormData } from '../SignupForm/SignupForm';
import signupFormStyles from '../SignupForm/SignupForm.module.css';
import styles from './CompanyDetails.module.css';
import { TurnoverInput } from './TurnoverInput';

type CompanyOption = {
  label: string;
  value: GetCompanySearchResponse['data'][number]['company_type'];
};

const COMPANY_TYPE_OPTIONS: CompanyOption[] = [
  {
    label: 'Limited Company',
    value: 'limited_liability_company',
  },
  {
    label: 'Limited Liability Partnership',
    value: 'limited_liability_partnership',
  },
  {
    label: 'Public Limited Company',
    value: 'public_limited_company',
  },
  { label: 'Other', value: 'other' },
];

export const CompanyDetails = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext<TSignupFormData>();
  const { stateData } = useSignupData();

  return (
    <Card className={signupFormStyles.formSection}>
      <h2>Company details</h2>
      <div className={signupFormStyles.cardContents}>
        {/* Introducers coming through the API might have missed this information off,
      if they come through step 0 they won't see this */}
        {!stateData.companyType && (
          <div className={signupFormStyles.formField}>
            <Input
              {...register('companyName')}
              label="Company name"
              id="companyName"
              error={errors.companyName && errors.companyName.message}
            />
          </div>
        )}
        {/* Introducers coming through the API might have missed this information off,
      if they come through step 0 they won't see this */}
        {!stateData.companyNumber && (
          <div className={signupFormStyles.formField}>
            <Input
              {...register('companyNumber')}
              label="Company number"
              id="companyNumber"
              error={errors.companyNumber && errors.companyNumber.message}
            />
          </div>
        )}
        <div className={signupFormStyles.formField}>
          <div className={signupFormStyles.labelWithTooltip}>
            <Label htmlFor="companyType">Company type</Label>
            <Tooltip content="This information is listed on Companies House." />
          </div>
          <Select
            {...register('companyType')}
            placeholder="Select your company type"
            id="companyType"
            className={signupFormStyles.select}
            error={errors.companyType && errors.companyType.message}
            disabled={Boolean(stateData.companyType)}
          >
            {COMPANY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <div className={signupFormStyles.formField}>
          <div className={signupFormStyles.labelWithTooltip}>
            <Label>Trading start date</Label>
            <Tooltip content="This information is listed on Companies House." />
          </div>
          <div className={styles.tradingFromContainer}>
            <Select
              {...register('startDateMonth')}
              data-testid="startDateMonth"
              placeholder="Month..."
              className={signupFormStyles.select}
              error={errors.startDateMonth && errors.startDateMonth.message}
              disabled={Boolean(stateData.startDateMonth)}
            >
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December </option>
            </Select>
            <Input
              {...register('startDateYear')}
              data-testid="startDateYear"
              placeholder="2000"
              error={
                errors?.startDateYear?.message && errors.startDateYear.message
              }
              disabled={Boolean(stateData.startDateYear)}
            />
          </div>
        </div>

        <div className={signupFormStyles.formField}>
          <TurnoverInput />
        </div>
        <div className={signupFormStyles.formField}>
          <Label>VAT registration status</Label>
          <div className={signupFormStyles.radioOptions}>
            <Radio
              {...register('isVatRegistered')}
              value="true"
              label="Registered"
            />
            <Radio
              {...register('isVatRegistered')}
              value="false"
              label="Not registered"
            />
          </div>
          {errors.isVatRegistered && (
            <Message
              className={styles.vatErrorMessage}
              message={errors.isVatRegistered.message || ''}
              type="error"
            />
          )}
        </div>
        <div className={signupFormStyles.formField}>
          <Input
            {...register('tradingName')}
            label="Trading name (optional)"
            description="Enter if the trading name is different from registered name"
          />
        </div>
        <div className={signupFormStyles.formField}>
          <Input
            {...register('website')}
            label="Website (optional)"
            placeholder="e.g. www.iwoca.co.uk"
            error={errors?.website?.message && errors.website.message}
          />
        </div>
      </div>
    </Card>
  );
};
